import { Button, Toast } from '@trackman/web-shared-components';
import { ServiceTypes } from 'constants/ServiceTypes';
import { SUPPORT_PHONE_NUMBER } from 'constants/SupportPhoneNumber';
import { ReactComponent as InfoIcon } from 'images/infoIcon.svg';
import React, { useEffect, useState } from 'react';
import { PitchComService } from 'services/data-services/PitchComService';
import { useInjection } from 'services/ioc/ioc.react';
import { ButtonsWrapper, Controls, ControlsInfo, InfoWrapper, ModalContent, StyledModal } from './PitchComModalStyles';

interface IProps {
  open: boolean;
  onDismiss: () => void;
}

export enum PitchComTriggerType {
  Strike = 'Strike',
  Ball = 'Ball',
  NotTracked = 'NotTracked',
}

type ToastSeverity = 'info' | 'success' | 'error';

const loadingDefaultState = {
  value: false,
  type: PitchComTriggerType.Strike,
};

const toastContentDefaultState = {
  severity: 'success' as ToastSeverity,
  header: '',
  message: '',
};

const PitchComModal: React.FC<IProps> = (props) => {
  const { open, onDismiss } = props;
  const [isLoading, setIsLoading] = useState<{ value: boolean; type: PitchComTriggerType }>(loadingDefaultState);
  const pitchComService = useInjection<PitchComService>(ServiceTypes.PitchComService);
  const [toastVisible, setToastVisible] = useState(false);
  const [toastContent, setToastContent] = useState(toastContentDefaultState);

  const closeHandler = () => {
    onDismiss();
  };

  const clickHandler = async (type: PitchComTriggerType) => {
    setIsLoading({ type: type, value: true });

    await pitchComService.sendTestRequest(type).then((res) => {
      if (res) {
        setToastContent({
          header: res.success ? 'Success' : 'Error',
          message: res.success ? 'Signal sent to device' : res.errorMessage,
          severity: res.success ? 'success' : 'error',
        });
      } else {
        setToastContent({
          header: 'Error',
          message: 'Failed to sent test request',
          severity: 'error',
        });
      }
      setToastVisible(true);
      setIsLoading({ type: type, value: false });
    });
  };

  const toastCloseHandler = () => {
    setToastVisible(false);
  };

  useEffect(() => {
    return () => {
      setToastVisible(false);
      setIsLoading(loadingDefaultState);
      setToastContent(toastContentDefaultState);
    };
  }, []);

  return (
    <StyledModal
      open={open}
      onClose={closeHandler}
      title='Pitchcom service test'
      confirmBtn={{ hidden: true, onClick: () => {} }}
      cancelBtn={{ onClick: closeHandler, style: { backgroundColor: '#F5F5F5', color: '#414141' } }}
    >
      <ModalContent>
        <p>To test your PitchCom device, click on any of the options below.</p>
        <div>
          <Controls>
            <ControlsInfo>Triggers to send:</ControlsInfo>
            <ButtonsWrapper>
              <Button
                isLoading={isLoading.type === PitchComTriggerType.Strike && isLoading.value}
                intent='secondary'
                label={PitchComTriggerType.Strike}
                onClick={() => clickHandler(PitchComTriggerType.Strike)}
                disabled={isLoading.value}
              />
              <Button
                isLoading={isLoading.type === PitchComTriggerType.Ball && isLoading.value}
                intent='secondary'
                label={PitchComTriggerType.Ball}
                onClick={() => clickHandler(PitchComTriggerType.Ball)}
                disabled={isLoading.value}
              />
              <Button
                isLoading={isLoading.type === PitchComTriggerType.NotTracked && isLoading.value}
                intent='secondary'
                label={PitchComTriggerType.NotTracked}
                onClick={() => clickHandler(PitchComTriggerType.NotTracked)}
                disabled={isLoading.value}
              />
            </ButtonsWrapper>
          </Controls>
          <InfoWrapper>
            <InfoIcon />
            <div>
              <p>If you are unable to hear any of these options, please switch out the PitchCom device with another and try again.</p>
              <p>If you're still unable to hear any of these options then call Support at {SUPPORT_PHONE_NUMBER}</p>
            </div>
          </InfoWrapper>
        </div>
      </ModalContent>
      {toastVisible && (
        <Toast
          open={toastVisible}
          onClose={toastCloseHandler}
          severity={toastContent.severity}
          header={toastContent.header}
          message={toastContent.message}
        />
      )}
    </StyledModal>
  );
};

export default PitchComModal;
